import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Send, Copy, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { STORAGE_KEYS, DEFAULT_MESSAGE_TEMPLATE } from '../mock';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { servicesAPI, profileAPI } from '../services/api';

const WhatsAppMessageEditor = ({ appointment, onClose, onSend }) => {
  const [message, setMessage] = useState('');
  const [confirmLink, setConfirmLink] = useState('');
  const [cancelLink, setCancelLink] = useState('');
  const [isShortening, setIsShortening] = useState(true);
  const [services, setServices] = useState([]);

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    if (services.length >= 0) { // Wait for services to load (even if empty)
      loadMessageTemplate();
    }
  }, [appointment, services]);

  const loadServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
      setServices([]); // Set empty array on error
    }
  };

  const shortenUrl = async (longUrl) => {
    try {
      // Use is.gd free URL shortener
      const response = await fetch(`https://is.gd/create.php?format=json&url=${encodeURIComponent(longUrl)}`);
      const data = await response.json();
      if (data.shorturl) {
        return data.shorturl;
      }
    } catch (error) {
      console.log('URL shortener failed, using original URL');
    }
    return longUrl; // Fallback to original if shortener fails
  };

  const loadMessageTemplate = async () => {
    setIsShortening(true);
    
    // Get user profile from API for additional info and templates
    let userProfile = {};
    try {
      const response = await profileAPI.get();
      userProfile = response.data;
    } catch (error) {
      console.error('Error loading profile:', error);
      // Use empty object as fallback
    }
    
    // Generate SHORT confirmation links with correct ID
    const appointmentId = appointment._id || appointment.id;
    const baseUrl = window.location.origin;
    const encodedId = btoa(appointmentId).replace(/=/g, ''); // Full base64 without padding
    const longConfirmUrl = `${baseUrl}/c?i=${encodedId}&a=1`;
    const longCancelUrl = `${baseUrl}/c?i=${encodedId}&a=0`;
    
    // Try to shorten URLs (parallel requests)
    const [confirmUrl, cancelUrl] = await Promise.all([
      shortenUrl(longConfirmUrl),
      shortenUrl(longCancelUrl)
    ]);
    
    setConfirmLink(confirmUrl);
    setCancelLink(cancelUrl);
    setIsShortening(false);
    
    // Use custom template or default
    let template = userProfile.reminderTemplate || '¡Hola {nombre}! 👋\n\nTe recordamos tu cita:\n\n📅 {fecha}\n🕐 {hora}\n📋 {servicio}\n{servicio_duracion}\n{servicio_descripcion}\n\n{negocio_especialidad}\n\n{direccion}\n\nPor favor confirma tu asistencia usando los enlaces a continuación.\n\n¡Te esperamos!';
    
    // Replace variables
    const firstName = appointment.patientName.split(' ')[0];
    template = template.replace('{nombre}', firstName);
    
    // Date formatting with day of week
    const appointmentDate = new Date(appointment.date + 'T00:00:00'); // Force local timezone
    const dayName = format(appointmentDate, 'EEEE', { locale: es }).toUpperCase();
    const dateFormatted = `${dayName} ${format(appointmentDate, 'd')}`;
    template = template.replace('{fecha}', dateFormatted);
    
    // Format time to 12-hour
    const [hours, minutes] = appointment.time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    const timeFormatted = `${hour12}:${minutes} ${ampm}`;
    template = template.replace('{hora}', timeFormatted);
    
    // Service details
    const appointmentServiceName = appointment.serviceType || 'Consulta';
    const serviceDetails = services.find(s => s.name === appointmentServiceName);
    
    let serviceText = appointmentServiceName;
    let serviceDuration = '';
    let serviceDescription = '';
    
    if (userProfile.reminderIncludeServiceDetails !== false && serviceDetails) {
      serviceDuration = serviceDetails.duration ? `⏱️ ${serviceDetails.duration} minutos` : '';
      serviceDescription = serviceDetails.description || '';
    }
    
    template = template.replace('{servicio}', serviceText);
    template = template.replace('{servicio_duracion}', serviceDuration);
    template = template.replace('{servicio_descripcion}', serviceDescription);
    
    // Business name and specialty
    let businessSpecialtyText = '';
    if (userProfile.businessName) {
      businessSpecialtyText += `🏥 ${userProfile.businessName}`;
    }
    if (userProfile.speciality) {
      businessSpecialtyText += userProfile.businessName ? ` - ${userProfile.speciality}` : `👨‍⚕️ ${userProfile.speciality}`;
    }
    template = template.replace('{negocio_especialidad}', businessSpecialtyText);
    
    // Address - placeholder if not set
    template = template.replace('{direccion}', '');
    
    // Clean up multiple empty lines
    template = template.replace(/\n\n\n+/g, '\n\n').trim();
    
    // Add confirmation buttons at the end
    let finalMessage = template + '\n\n';
    finalMessage += `┌──────────────┐\n`;
    finalMessage += `│ ✅ CONFIRMAR │ → ${confirmUrl}\n`;
    finalMessage += `└──────────────┘\n\n`;
    finalMessage += `┌──────────────┐\n`;
    finalMessage += `│ ❌ CANCELAR  │ → ${cancelUrl}\n`;
    finalMessage += `└──────────────┘`;
    
    setMessage(finalMessage);
  };

  const generateToken = (apt) => {
    return btoa(`${apt.id}-${apt.date}-${apt.time}`).substring(0, 16);
  };

  const handleSend = async () => {
    try {
      // Copy message to clipboard
      await navigator.clipboard.writeText(message);
      
      // Open WhatsApp Business
      const phone = appointment.phone.replace(/[^0-9]/g, '');
      const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
      
      toast({
        title: '✅ Mensaje copiado',
        description: 'WhatsApp Business abierto con el mensaje listo para enviar',
        duration: 4000,
      });
      
      if (onSend) onSend();
      if (onClose) onClose();
      
    } catch (error) {
      // Fallback
      const phone = appointment.phone.replace(/[^0-9]/g, '');
      const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
      
      if (onClose) onClose();
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast({
        title: '📋 Mensaje copiado',
        description: 'El mensaje se copió al portapapeles',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo copiar el mensaje',
        variant: 'destructive',
      });
    }
  };

  const copyLink = async (link, type) => {
    try {
      await navigator.clipboard.writeText(link);
      toast({
        title: '🔗 Link copiado',
        description: `Link de ${type} copiado al portapapeles`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo copiar el link',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[95vh] overflow-y-auto">
        <CardHeader className="bg-gradient-to-r from-green-500 to-blue-500 text-white">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl">✉️ Personalizar Mensaje de WhatsApp</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* Patient Info - Compact */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50 rounded border border-blue-200">
            <div className="space-y-1 text-xs">
              <p><strong>Paciente:</strong> {appointment.patientName}</p>
              <p><strong>Teléfono:</strong> {appointment.phone}</p>
              <p><strong>Servicio:</strong> {appointment.serviceType}</p>
            </div>
            <div className="space-y-1 text-xs">
              <p><strong>Fecha:</strong> {format(new Date(appointment.date + 'T00:00:00'), 'EEEE d', { locale: es }).toUpperCase()}</p>
              <p><strong>Hora:</strong> {appointment.time}</p>
            </div>
          </div>

          {/* Message Editor - Optimized */}
          <div className="space-y-3">
            <div className="flex justify-between items-start">
              <div>
                <Label htmlFor="message" className="text-base font-semibold">Mensaje de Recordatorio</Label>
                <p className="text-xs text-gray-600 mt-0.5">Edita el mensaje con tu dirección, GPS y recomendaciones</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={copyMessage}
              >
                <Copy className="w-4 h-4 mr-1" />
                Copiar
              </Button>
            </div>
            
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={16}
              className="text-sm leading-relaxed"
              placeholder="Escribe aquí tu mensaje..."
            />
            
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded border text-xs">
              <div className="flex items-center space-x-4">
                <span className={`font-semibold ${message.length > 2000 ? 'text-red-600' : message.length > 1500 ? 'text-orange-600' : 'text-green-600'}`}>
                  {message.length.toLocaleString()} caracteres
                </span>
                <span className="text-gray-600">
                  {message.split('\n').length} líneas
                </span>
              </div>
              <div>
                {message.length > 2000 && (
                  <span className="text-red-600">⚠️ Muy largo</span>
                )}
                {message.length > 1500 && message.length <= 2000 && (
                  <span className="text-orange-600">⚠️ Largo</span>
                )}
                {message.length <= 1500 && (
                  <span className="text-green-600">✅ Óptimo</span>
                )}
              </div>
            </div>
            
            <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded border border-blue-200">
              💡 Los links de confirmación/cancelación ya están incluidos. Edita el mensaje libremente.
            </p>
          </div>

          {/* Confirmation Links - Compact */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">🔗 Links de confirmación (incluidos en el mensaje):</h3>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                <div className="flex-1 mr-1 overflow-hidden">
                  <p className="text-xs font-semibold text-green-800 mb-0.5">✅ Confirmar</p>
                  <p className="text-xs text-gray-600 truncate">{confirmLink}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyLink(confirmLink, 'confirmación')}
                  className="h-7 w-7 p-0"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>

              <div className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-200">
                <div className="flex-1 mr-1 overflow-hidden">
                  <p className="text-xs font-semibold text-red-800 mb-0.5">❌ Cancelar</p>
                  <p className="text-xs text-gray-600 truncate">{cancelLink}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyLink(cancelLink, 'cancelación')}
                  className="h-7 w-7 p-0"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button
              onClick={handleSend}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white font-medium py-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Send className="w-5 h-5 mr-2" />
              Enviar por WhatsApp
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 py-6"
            >
              Cancelar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsAppMessageEditor;